/**
 * Projects Table Module - DataTable Implementation
 * Uses the shared DataTable component with projects-specific customizations
 */

import DataTable from '../common/components/DataTable.js';
import { setupSlimSelect } from '../common/manageForm';
import { StringUtil } from '../common/util/StringUtil';
import { SelectionManager } from './manageExport';

/**
 * Initialize the projects table using DataTable component
 */
export function initializeProjectsTable() {
    const dataElem = document.getElementById('page-data');
    if (!dataElem) return null;

    const config = JSON.parse(dataElem.textContent);
    const data = config.payload || [];
    const idKey = config.id_key || 'id';

    // Transform columns from old format to new format
    const columns = config.columns.map(col => {
        const key = Array.isArray(col) ? col[0] : col;

        // Custom label for short_title
        let label = key === 'short_title' ? 'Short title' : StringUtil.camelToPretty(key);

        return {
            key: key,
            label: label,
            sortable: true,
            formatter: getColumnFormatter(key),
            className: key === 'short_title' ? 'project-title-cell' : undefined
        };
    });

    // Build filters configuration for DataTable
    const filters = buildFiltersConfig(config.filters, config.loc_hierarchy);

    // Default sort for projects
    const defaultSort = config.name === 'projects'
        ? { field: 'start', direction: 'desc' }
        : null;

    // Variable to hold checkbox reference for customFilterFn
    let onlySelectedCheckbox = null;

    // Store reference to table for use in custom filter
    let tableInstance = null;

    // Initialize DataTable with custom filter for "only selected" checkbox
    const table = new DataTable('projectsTable', {
        data: data,
        columns: columns,
        filters: filters,
        pageSize: 15,
        sortable: true,
        selectable: config.selectable || false,
        idKey: idKey,
        stateKey: `projects_table_${config.name}`,
        defaultSort: defaultSort,
        verboseFields: config.verbose_fields,
        showMatchTooltip: true,
        customFilterFn: (row, currentFilters) => {
            // Apply "only selected" filter if checkbox is checked
            if (onlySelectedCheckbox && onlySelectedCheckbox.checked) {
                if (tableInstance && !tableInstance.getSelectedIds().includes(row[idKey])) {
                    return false;
                }
            }

            // Apply normal filters using DataTable's filter matching logic
            for (const filterConfig of filters) {
                const filterValue = currentFilters[filterConfig.key];

                // Skip empty filter values
                if (!filterValue) continue;
                if (Array.isArray(filterValue) && filterValue.length === 0) continue;
                if (typeof filterValue === 'object' && !Array.isArray(filterValue)) {
                    // For daterange filters with empty from/to
                    if (!filterValue.from && !filterValue.to) continue;
                }

                // If tableInstance isn't ready yet, skip filter validation (initial render)
                if (!tableInstance) continue;

                const matches = tableInstance.filters.matchesFilter(row, filterConfig, filterValue);

                if (!matches) {
                    return false;
                }
            }

            // Handle location filter separately (it's not in the filters config)
            const locationFilter = currentFilters['location-search'];
            if (locationFilter && Array.isArray(locationFilter) && locationFilter.length > 0) {
                const rowLocations = row.locations;

                if (!rowLocations) {
                    return false;
                }

                // Check if row locations (comma-separated string or array) matches any selected location
                let locationMatches = false;
                if (typeof rowLocations === 'string' && rowLocations.includes(',')) {
                    const locations = rowLocations.split(',').map(v => v.trim());
                    locationMatches = locationFilter.some(selected =>
                        locations.some(loc => loc === selected)
                    );
                } else if (Array.isArray(rowLocations)) {
                    locationMatches = locationFilter.some(selected =>
                        rowLocations.includes(selected)
                    );
                } else {
                    locationMatches = locationFilter.includes(String(rowLocations));
                }

                if (!locationMatches) {
                    return false;
                }
            }

            return true;
        },
        onRowClick: config.on_click_url ? (rowData, event) => {
            const url = '/' + config.on_click_url + rowData[idKey];
            DataTable.navigateWithModifiers(url, event);
        } : null,
        onSelectionChange: (selectedIds, selectedRows) => {
            // Update selection count display
            const selectedCountEl = document.getElementById('selected-projects');
            if (selectedCountEl) {
                selectedCountEl.textContent = selectedIds.length;
            }

            // Dispatch event for export module and other listeners
            document.dispatchEvent(new CustomEvent('projects:selection-changed', {
                detail: { selectedIds, selectedRows }
            }));
        },
        rowClassFn: (rowData) => {
            const classes = [];

            // Add missing data indicator
            if (rowData.missing_major) {
                classes.push('missing-major');
            }

            // Add parent/child indicators
            if (rowData.is_parent && rowData.is_children) {
                classes.push('parent', 'children');
            } else if (rowData.is_parent) {
                classes.push('parent');
            } else if (rowData.is_children) {
                classes.push('children');
            }

            return classes;
        }
    });

    // Store table instance for use in custom filter
    tableInstance = table;

    // Re-apply filters now that tableInstance is available
    // This is needed because the custom filter function requires tableInstance
    // but it's called during initialization before tableInstance is set
    const currentFilters = table.state.get('currentFilters');
    const hasActiveFilters = Object.values(currentFilters).some(val =>
        val && (Array.isArray(val) ? val.length > 0 : true)
    );
    if (hasActiveFilters) {
        table._applyFiltersAndRender();
    }

    // Setup location filter with hierarchy if provided
    if (config.loc_hierarchy) {
        const locFilter = document.querySelector('[name="location-search"]');
        if (locFilter) {
            // Initialize location filter state in DataTable
            const currentFilters = table.state.get('currentFilters');
            if (!currentFilters['location-search']) {
                currentFilters['location-search'] = [];
            }

            const locSelect = setupSlimSelect(locFilter, false, config.loc_hierarchy);
            window.locationSlimSelect = locSelect; // Store for map integration

            // Hook up location filter to DataTable's filter system
            // Note: Location filter is handled separately from normal filters because:
            // 1. It checks row.locations field (not row['location-search'])
            // 2. It uses hierarchical data structure (zones -> countries)
            // 3. It needs manual state management and event handling
            const handleLocationChange = () => {
                const selectedLocations = Array.from(locFilter.selectedOptions).map(opt => opt.value);
                const currentFilters = table.state.get('currentFilters');
                currentFilters['location-search'] = selectedLocations;
                table.state.update('currentPage', 1);
                table._applyFiltersAndRender();
            };

            // Attach to both native change event and SlimSelect afterChange
            locFilter.addEventListener('change', handleLocationChange);

            if (locSelect && locSelect.events) {
                const originalAfterChange = locSelect.events.afterChange;
                locSelect.events.afterChange = (newVal) => {
                    handleLocationChange();
                    if (originalAfterChange && typeof originalAfterChange === 'function') {
                        originalAfterChange(newVal);
                    }
                };
            }

            // Hook into reset button to also clear location filter
            const resetButton = document.querySelector('[data-action="reset-filters"]');
            if (resetButton) {
                resetButton.addEventListener('click', () => {
                    setTimeout(() => {
                        const currentFilters = table.state.get('currentFilters');
                        currentFilters['location-search'] = [];
                        if (locSelect) {
                            locSelect.setSelected([]);
                        }
                        table._applyFiltersAndRender();
                    }, 0);
                });
            }
        }
    }

    // Create and add "only selected" checkbox if configured
    if (config.filter_selected) {
        const wrapper = document.getElementById('only-selected-wrapper');
        if (wrapper) {
            wrapper.className = 'filter-checkbox';
            wrapper.style.display = 'none'; // Hidden by default

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = 'only-selected';

            const labelEl = document.createElement('label');
            labelEl.htmlFor = 'only-selected';
            labelEl.textContent = ' Only display selected';

            wrapper.appendChild(checkbox);
            wrapper.appendChild(labelEl);

            onlySelectedCheckbox = checkbox;

            // Add change listener to trigger DataTable's filter system
            checkbox.addEventListener('change', () => {
                table._applyFiltersAndRender(); // Trigger DataTable's filtering
            });
        }
    }

    // Store pending selections promise for synchronization
    let pendingSelectionsPromise = null;
    const waitForPendingSelections = () => {
        if (pendingSelectionsPromise) {
            // Wait for previous pending selections to complete first
            return pendingSelectionsPromise;
        }
        return Promise.resolve();
    };

    // Return API compatible with old Table.js for SelectionManager
    const tableAPI = {
        table: {
            getData: (type) => {
                switch (type) {
                    case 'active':
                    case 'filtered':
                        return table.getFilteredData();
                    case 'visible':
                        return table._getCurrentPageData();
                    default:
                        return data;
                }
            },
            getRows: (type = 'all') => {
                // Get data based on type
                let rowsData;
                switch (type) {
                    case 'active':
                    case 'filtered':
                        rowsData = table.getFilteredData();
                        break;
                    case 'visible':
                        rowsData = table._getCurrentPageData();
                        break;
                    default:
                        rowsData = data;
                }

                // Batching mechanism for select/deselect operations
                // Collect IDs to select/deselect, then apply in batch
                const pendingSelections = {
                    select: new Set(),
                    deselect: new Set(),
                    applied: false
                };

                let promiseResolve;
                const batchPromise = new Promise(resolve => {
                    promiseResolve = resolve;
                });

                const applyPendingSelections = () => {
                    if (pendingSelections.applied) return;
                    pendingSelections.applied = true;

                    // Deselect first, then select to avoid conflicts
                    if (pendingSelections.deselect.size > 0) {
                        table.deselectRows([...pendingSelections.deselect]);
                    }
                    if (pendingSelections.select.size > 0) {
                        table.selectRows([...pendingSelections.select]);
                    }

                    // Resolve the promise so filterSelectedIds can proceed
                    if (promiseResolve) {
                        promiseResolve();
                    }
                    pendingSelectionsPromise = null;
                };

                // Return row objects with select/deselect methods that batch operations
                const rows = rowsData.map(rowData => ({
                    getData: () => rowData,
                    select: () => {
                        pendingSelections.select.add(rowData[idKey]);
                        pendingSelections.deselect.delete(rowData[idKey]); // Remove from deselect if present
                    },
                    deselect: () => {
                        pendingSelections.deselect.add(rowData[idKey]);
                        pendingSelections.select.delete(rowData[idKey]); // Remove from select if present
                    },
                    isSelected: () => table.getSelectedIds().includes(rowData[idKey])
                }));

                // Store the promise for synchronization with filterSelectedIds
                pendingSelectionsPromise = batchPromise;

                // Apply batched changes after current execution context
                // This allows the forEach loop in selectFromIds to complete first
                setTimeout(applyPendingSelections, 0);

                return rows;
            },
            getSelectedRows: () => {
                return table.getSelectedRows().map(rowData => ({
                    getData: () => rowData,
                    id: rowData[idKey]
                }));
            },
            clearSelection: () => table.clearSelection(),
            selectRows: (ids) => table.selectRows(ids)
        },
        getIds: () => table.getSelectedIds(),
        filterSelectedIds: () => {
            // Wait for any pending selections to be applied first
            waitForPendingSelections().then(() => {
                const selectedIds = table.getSelectedIds();

                // Show/hide and check/uncheck the checkbox based on selections
                if (onlySelectedCheckbox) {
                    const wrapper = onlySelectedCheckbox.parentElement;

                    if (selectedIds.length > 0) {
                        wrapper.style.display = 'flex';
                        // Check the checkbox when showing it (selection just loaded)
                        onlySelectedCheckbox.checked = true;
                    } else {
                        wrapper.style.display = 'none';
                        onlySelectedCheckbox.checked = false;
                    }

                    // Trigger DataTable's filter system to apply the checkbox filter
                    table._applyFiltersAndRender();
                }
            });
        },
        onlySelectedCheckbox: onlySelectedCheckbox, // Expose for SelectionManager
        dataTable: table // Expose DataTable instance for map integration
    };

    // Initialize SelectionManager if save_available is enabled
    if (config.name === 'projects' && config.save_available) {
        new SelectionManager(tableAPI);
    }

    return tableAPI;
}

/**
 * Build filters configuration from old format
 */
function buildFiltersConfig(oldFilters, locHierarchy) {
    const filters = [];
    const otherFilters = [];

    if (!oldFilters) return filters;

    // Process filters, separating text search from others
    oldFilters.forEach((filterDef, index) => {
        // Text search filter - will be added first
        if (filterDef.comp === 'text') {
            // Generate unique key: use first field key, or 'search' for general search
            const key = filterDef.keys.length === 1 ? filterDef.keys[0] :
                        (index === 0 ? 'search' : `search_${filterDef.keys[0]}`);

            filters.push({
                key: key,
                type: 'text',
                placeholder: filterDef.placeholder || filterDef.label || 'Search for text',
                searchFields: filterDef.keys,
                label: filterDef.label
            });
        }
        // Multiselect filters
        else if (filterDef.type === 'multiple') {
            otherFilters.push({
                key: filterDef.keys[0], // Use first key
                type: 'multiselect',
                label: filterDef.label,
                // Options will be extracted from data by DataTable
            });
        }
        // Date filters
        else if (filterDef.type === 'month') {
            otherFilters.push({
                key: filterDef.keys[0],
                type: 'date',
                label: filterDef.label
            });
        }
        // Number filters
        else if (filterDef.type === 'number') {
            otherFilters.push({
                key: filterDef.keys[0],
                type: 'number',
                label: filterDef.label
            });
        }
    });

    // NOTE: Location filter is NOT added here because it's handled separately
    // in the customFilterFn. It uses setupSlimSelect and checks row.locations field.
    // The state is initialized manually when setting up the location filter.

    // Text search first, then other filters
    return [...filters, ...otherFilters];
}

/**
 * Get column formatter for specific column keys
 */
function getColumnFormatter(key) {
    // Date formatters
    if (key === 'start' || key === 'end') {
        return (value) => {
            if (!value) return '<span style="color: #999;">-</span>';
            const date = new Date(value);
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        };
    }

    // Short title formatter with parent/child indicators and tooltip
    if (key === 'short_title') {
        return (value, rowData) => {
            let displayTitle = value || rowData.title || `Project ${rowData.id}`;
            let appendix = '';

            if (rowData.is_parent && rowData.is_children) {
                appendix = ' (Parent and child)';
            } else if (rowData.is_parent) {
                appendix = ' (Parent)';
            } else if (rowData.is_children) {
                appendix = ' (Child)';
            }

            const fullText = displayTitle + appendix;

            // Create span with ellipsis and tooltip
            return `<span class="project-title-ellipsis" title="${fullText.replace(/"/g, '&quot;')}">${fullText}</span>`;
        };
    }

    // Deliverable file with download link
    if (key === 'deliverable_file') {
        return (value, rowData) => {
            if (!value || !rowData.deliverable_url) return '<span style="color: #999;">-</span>';
            return `<a href="${rowData.deliverable_url}" target="_blank" title="Download deliverable" onclick="event.stopPropagation()"><i class="fas fa-download me-1"></i>${value}</a>`;
        };
    }

    // Default: no formatting
    return null;
}

export default initializeProjectsTable;
